import axios from "axios";

const creditUrl = "/v2/api/credits";

class CreditService {
    // Search for all credits with optional query parameters
    static searchAllCredits(urlParams) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}${urlParams}`;
                const result = await axios.get(url);
                const pagingData = result.data;
                resolve(pagingData);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Get a specific credit by its ID
    static getCreditById(creditId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}`;
                const result = await axios.get(url);
                const creditData = result.data;
                resolve(creditData);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Create a new credit
    static createCredit(creditData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = creditUrl;
                const result = await axios.post(url, creditData);
                const createdCredit = result.data;
                resolve(createdCredit);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Update an existing credit
    static updateCredit(creditId, updatedCreditData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}`;
                await axios.put(url, updatedCreditData);
                resolve(`Credit with ID ${creditId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Delete a credit by its ID
    static deleteCredit(creditId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}`;
                await axios.delete(url);
                resolve(`Credit with ID ${creditId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Create a credit item for a specific credit
    static createCreditItem(creditId, creditItemData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/items`;
                const result = await axios.post(url, creditItemData);
                const createdCreditItem = result.data;
                resolve(createdCreditItem);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Update an existing credit item
    static updateCreditItem(creditId, creditItemId, updatedCreditItemData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/items/${creditItemId}`;
                await axios.put(url, updatedCreditItemData);
                resolve(`Credit item with ID ${creditItemId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Delete a credit item from a specific credit
    static deleteCreditItem(creditId, creditItemId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/items/${creditItemId}`;
                await axios.delete(url);
                resolve(`Credit item with ID ${creditItemId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Generate a credit from an invoice
    static convertFromInvoice(invoiceId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/convert-from-invoice/${invoiceId}`;
                const result = await axios.post(url);
                const convertedCredit = result.data;
                resolve(convertedCredit);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // List credits with remaining balance available for a given contact
    static getAvailableCreditsForContact(contactId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/available/${contactId}`;
                const result = await axios.get(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Apply an existing credit (or part of it) against an invoice
    static applyToInvoice(creditId, invoiceId, amount) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/apply`;
                const result = await axios.post(url, { invoice_id: invoiceId, amount });
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Issue an invoice-correction credit note, applying it back to its originating invoice.
    // matchCurrentOutstanding (default true): rescale down to what the invoice currently owes if
    // other credit activity has reduced it since; false issues the credit note exactly as created.
    static finalizeCredit(creditId, matchCurrentOutstanding = true) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/finalize`;
                const result = await axios.post(url, { matchCurrentOutstanding });
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Void a credit: it stays on record but can no longer be used or issued
    static voidCredit(creditId, reason) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/void`;
                const result = await axios.post(url, { reason });
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // What has already been credited against an invoice and how much more can be credited
    static getInvoiceCreditSummary(invoiceId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/invoice/${invoiceId}/summary`;
                const result = await axios.get(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Reopen an expired open credit: a new expiry (YYYY-MM-DD) and the reason it is being reopened
    static extendCreditExpiry(creditId, expiryDate, reason) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/extend-expiry`;
                const result = await axios.post(url, { expiry_date: expiryDate, reason });
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Read-only: check what amount would actually be applied before committing to it
    static previewFinalizeCredit(creditId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${creditUrl}/${creditId}/finalize-preview`;
                const result = await axios.get(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Generate a credit report
    static async generateCreditReport(creditId, reportType) {
        try {
            const url = `${creditUrl}/${creditId}/report/${reportType}`;
            const response = await axios.post(url, {}, { responseType: "blob" });
            return response.data;
        } catch (error) {
            throw new Error(error.response ? error.response.data : error.message);
        }
    }
}

export default CreditService;
